<script setup lang="ts">
  defineOptions({
    name: 'ChatConversationActionConfirmations',
  })

  import { computed, type PropType } from 'vue'

  import { useI18n } from 'vue-i18n'

  import { SlashCircle01Icon, Trash01Icon } from '@gohighlevel/ghl-icons/24/outline'

  import { AnalyticsEvents } from '@common/analytics-layer/types'
  import { createElementId } from '@common/core-layer/utils/index'
  import CPConfirmationModal from '@common/shared-ui-layer/components/CPConfirmationModal.vue'
  import CPButton from '@ui/components/CPButton.vue'
  import CPDrawer from '@ui/components/CPDrawer.vue'
  import CPText from '@ui/components/CPText.vue'

  const props = defineProps({
    activeConfirmation: {
      type: String as PropType<ChatConversationConfirmationType | null>,
      default: null,
    },
    recipientName: {
      type: String,
      default: '',
    },
    width: {
      type: Number,
      default: 500,
    },
    zIndex: {
      type: Number,
      default: 10030,
    },
    mobile: {
      type: Boolean,
      default: false,
    },
  })

  const emit = defineEmits<{
    'update:activeConfirmation': [value: ChatConversationConfirmationType | null]
    confirmBlock: []
    confirmDelete: []
  }>()

  const { t } = useI18n()

  const displayName = computed(() => props.recipientName || t('chats.user.unknownUser'))

  const showBlockConfirmation = computed(() => props.activeConfirmation === 'block')

  const showDeleteConfirmation = computed(() => props.activeConfirmation === 'delete')

  const blockModalContent = computed(() => ({
    title: t('chats.confirmation.blockTitle', { name: displayName.value }),
    message: t('chats.confirmation.blockMessage'),
    icon: SlashCircle01Icon,
    iconType: 'error' as const,
  }))

  const deleteModalContent = computed(() => ({
    title: t('chats.confirmation.deleteTitle'),
    message: t('chats.confirmation.deleteMessage', { name: displayName.value }),
    icon: Trash01Icon,
    iconType: 'error' as const,
  }))

  /**
   * Declarative GA binding for the mobile block drawer. The web variant renders its buttons from
   * the blockActions data array via CPModal, which exposes no element to bind, so that surface is
   * not covered declaratively yet.
   */
  const chatBlockedTrack = computed(() => ({ event: AnalyticsEvents.CHAT_BLOCKED }))

  const blockActions = computed(() => [
    {
      key: 'cancel',
      label: t('common.cancel'),
      type: 'secondary' as const,
    },
    {
      key: 'confirm',
      label: t('chats.confirmation.blockConfirm'),
      color: 'error' as const,
      type: 'primary' as const,
    },
  ])

  const deleteActions = computed(() => [
    {
      key: 'cancel',
      label: t('common.cancel'),
      type: 'secondary' as const,
    },
    {
      key: 'confirm',
      label: t('chats.confirmation.deleteConfirm'),
      color: 'error' as const,
      type: 'primary' as const,
    },
  ])

  const handleBlockAction = (actionKey: string) => {
    if (actionKey === 'confirm') {
      emit('confirmBlock')
    }
    emit('update:activeConfirmation', null)
  }

  const handleDeleteAction = (actionKey: string) => {
    if (actionKey === 'confirm') {
      emit('confirmDelete')
    }
    emit('update:activeConfirmation', null)
  }

  type ChatConversationConfirmationType = 'block' | 'delete'
</script>

<template>
  <!-- Desktop: center modal -->
  <template v-if="!mobile">
    <CPConfirmationModal
      v-if="showBlockConfirmation"
      :show="showBlockConfirmation"
      :content="blockModalContent"
      :actions="blockActions"
      :width="width"
      :z-index="zIndex"
      :show-close-button="true"
      :close-on-backdrop="true"
      @action="handleBlockAction"
      @close="emit('update:activeConfirmation', null)"
      @update:show="emit('update:activeConfirmation', $event ? 'block' : null)"
    />

    <CPConfirmationModal
      v-if="showDeleteConfirmation"
      :show="showDeleteConfirmation"
      :content="deleteModalContent"
      :actions="deleteActions"
      :width="width"
      :z-index="zIndex"
      :show-close-button="true"
      :close-on-backdrop="true"
      @action="handleDeleteAction"
      @close="emit('update:activeConfirmation', null)"
      @update:show="emit('update:activeConfirmation', $event ? 'delete' : null)"
    />
  </template>

  <!-- Mobile: bottom sheet drawers -->
  <template v-else>
    <!-- Block confirmation sheet -->
    <CPDrawer
      :id="createElementId('chats', 'block-confirmation', 'drawer')"
      :show="showBlockConfirmation"
      placement="bottom"
      height="320"
      :mask-closable="true"
      :closable="false"
      :show-header="true"
      :z-index="zIndex"
      header-style="border-bottom: none"
      body-class="p-0"
      body-content-class="!pt-0"
      footer-class="p-0"
      @update:show="emit('update:activeConfirmation', $event ? 'block' : null)"
    >
      <template #header>
        <div class="flex justify-center pb-2 pt-3">
          <div class="h-[0.1875rem] w-[3.125rem] rounded-full bg-hover" />
        </div>
      </template>

      <div class="flex flex-col items-center gap-4 px-6 py-4">
        <div
          class="my-1 flex h-12 w-12 items-center justify-center rounded-full border-8 border-solid border-error-50 bg-error-100 p-1 text-error-400"
        >
          <SlashCircle01Icon class="h-6 w-6" />
        </div>
        <CPText size="lg" weight="semibold" class="text-center text-body">
          {{ blockModalContent.title }}
        </CPText>
        <CPText size="md" class="text-center text-700">
          {{ blockModalContent.message }}
        </CPText>
      </div>

      <template #footer>
        <div class="flex flex-1 gap-2">
          <div class="flex-1">
            <CPButton
              :id="createElementId('chats', 'block-drawer', 'cancel')"
              type="secondary"
              color="secondary"
              size="md"
              class="w-full"
              @click="handleBlockAction('cancel')"
            >
              {{ t('common.cancel') }}
            </CPButton>
          </div>
          <div class="flex-1">
            <CPButton
              :id="createElementId('chats', 'block-drawer', 'confirm')"
              v-track="chatBlockedTrack"
              type="primary"
              color="error"
              size="md"
              class="w-full"
              @click="handleBlockAction('confirm')"
            >
              {{ t('chats.confirmation.blockConfirm') }}
            </CPButton>
          </div>
        </div>
      </template>
    </CPDrawer>

    <!-- Delete confirmation sheet -->
    <CPDrawer
      :id="createElementId('chats', 'delete-confirmation', 'drawer')"
      :show="showDeleteConfirmation"
      placement="bottom"
      height="320"
      :mask-closable="true"
      :closable="false"
      :show-header="true"
      :z-index="zIndex"
      header-style="border-bottom: none"
      body-class="p-0"
      body-content-class="!pt-0"
      footer-class="p-0"
      @update:show="emit('update:activeConfirmation', $event ? 'delete' : null)"
    >
      <template #header>
        <div class="flex justify-center pb-2 pt-3">
          <div class="h-[0.1875rem] w-[3.125rem] rounded-full bg-hover" />
        </div>
      </template>

      <div class="flex flex-col items-center gap-4 px-6 py-4">
        <div
          class="my-1 flex h-12 w-12 items-center justify-center rounded-full border-8 border-solid border-error-50 bg-error-100 p-1 text-error-400"
        >
          <Trash01Icon class="h-6 w-6" />
        </div>
        <CPText size="lg" weight="semibold" class="text-center text-body">
          {{ deleteModalContent.title }}
        </CPText>
        <CPText size="md" class="text-center text-700">
          {{ deleteModalContent.message }}
        </CPText>
      </div>

      <template #footer>
        <div class="flex flex-1 gap-2">
          <div class="flex-1">
            <CPButton
              :id="createElementId('chats', 'delete-drawer', 'cancel')"
              type="secondary"
              color="secondary"
              size="md"
              class="w-full"
              @click="handleDeleteAction('cancel')"
            >
              {{ t('common.cancel') }}
            </CPButton>
          </div>
          <div class="flex-1">
            <CPButton
              :id="createElementId('chats', 'delete-drawer', 'confirm')"
              type="primary"
              color="error"
              size="md"
              class="w-full"
              @click="handleDeleteAction('confirm')"
            >
              {{ t('chats.confirmation.deleteConfirm') }}
            </CPButton>
          </div>
        </div>
      </template>
    </CPDrawer>
  </template>
</template>
